import {
  FONTS,
  Fonts,
  MONOSPACE_FONTS,
  MonospaceFonts,
} from "@mutiny/ui/themes/fonts";
import { Accessor, createSignal } from "solid-js";
import { AbstractStore } from ".";
import { State } from "..";

export type TypeTheme = {
  mode: "light" | "dark" | "system";
  highContrast: boolean;
  density: "comfortable" | "compact" | "auto";
  /** Retained for old consumers; app chrome no longer uses vibrancy. */
  blur: boolean;
  interfaceFont: Fonts;
  monospaceFont: MonospaceFonts;
  messageSize: number;
  messageGroupSpacing: number;
};
export type SelectedTheme = TypeTheme & { darkMode: boolean };

/** Appearance preferences, not a palette generator. Unknown/custom data is ignored. */
export class Theme extends AbstractStore<"theme", TypeTheme> {
  prefersDark: Accessor<boolean>;

  constructor(state: State) {
    super(state, "theme");
    const media = window.matchMedia("(prefers-color-scheme: dark)");
    const [prefersDark, setPrefersDark] = createSignal(media.matches);
    this.prefersDark = prefersDark;
    media.addEventListener("change", (event) => setPrefersDark(event.matches));
  }

  hydrate(): void {
    /* preferences are hydrated by State */
  }

  default(): TypeTheme {
    return {
      mode: "system",
      highContrast: false,
      density: "auto",
      blur: false,
      interfaceFont: "Inter",
      monospaceFont: "Fira Code",
      messageSize: 14,
      messageGroupSpacing: 12,
    };
  }

  clean(input: Partial<TypeTheme>): TypeTheme {
    const data = this.default();
    // Explicit allowlist: never inspect legacy accent/variant/custom theme fields.
    if (["light", "dark", "system"].includes(input.mode!))
      data.mode = input.mode!;
    if (typeof input.highContrast === "boolean")
      data.highContrast = input.highContrast;
    if (["comfortable", "compact", "auto"].includes(input.density!))
      data.density = input.density!;
    if (
      typeof input.messageSize === "number" &&
      Number.isFinite(input.messageSize)
    )
      data.messageSize = Math.min(24, Math.max(12, input.messageSize));
    if (
      typeof input.messageGroupSpacing === "number" &&
      Number.isFinite(input.messageGroupSpacing)
    )
      data.messageGroupSpacing = Math.min(
        16,
        Math.max(0, input.messageGroupSpacing),
      );
    // Existing font choices remain independent of the canonical colour palette.
    if (input.interfaceFont && Object.hasOwn(FONTS, input.interfaceFont))
      data.interfaceFont = input.interfaceFont;
    if (
      input.monospaceFont &&
      Object.hasOwn(MONOSPACE_FONTS, input.monospaceFont)
    )
      data.monospaceFont = input.monospaceFont;
    return data;
  }

  get activeTheme(): SelectedTheme {
    const opts = this.get();
    return {
      ...opts,
      darkMode:
        opts.mode === "dark" || (opts.mode === "system" && this.prefersDark()),
    };
  }
  get mode() {
    return this.get().mode;
  }
  setMode(mode: TypeTheme["mode"]) {
    this.set("mode", mode);
  }
  get highContrast() {
    return this.get().highContrast;
  }
  setHighContrast(value: boolean) {
    this.set("highContrast", value);
  }
  get density() {
    return this.get().density;
  }
  setDensity(value: TypeTheme["density"]) {
    this.set("density", value);
  }
  get interfaceFont() {
    return this.get().interfaceFont;
  }
  setInterfaceFont(font: Fonts) {
    this.set("interfaceFont", font);
  }
  get monospaceFont() {
    return this.get().monospaceFont;
  }
  setMonospaceFont(font: MonospaceFonts) {
    this.set("monospaceFont", font);
  }
  get messageSize() {
    return this.get().messageSize;
  }
  set messageSize(size: number) {
    this.set("messageSize", size);
  }
  get messageGroupSpacing() {
    return this.get().messageGroupSpacing;
  }
  set messageGroupSpacing(space: number) {
    this.set("messageGroupSpacing", space);
  }
}
